import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Inbox, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ExportPreRegistrationsButton } from './ExportPreRegistrationsButton';
import { MunicipalListAccommodationsTable } from './MunicipalListAccommodationsTable';
import {
  PreRegisteredAccommodationSheet,
  type AccommodationSheetMode,
} from './PreRegisteredAccommodationSheet';
import { isRowDeleted } from '../utils/status-transitions.utils';
import type { MunicipalListDetail } from '../types/pre-registration.types';

// Query param used by links to one row (see preRegistrationRoutes.accommodation)
const ROW_PARAM = 'fila';

interface DialogState {
  mode: AccommodationSheetMode;
  rowId?: string;
}

interface MunicipalListAccommodationsSectionProps {
  list: MunicipalListDetail;
  canEdit: boolean;
}

export const MunicipalListAccommodationsSection: React.FC<MunicipalListAccommodationsSectionProps> = ({
  list,
  canEdit,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showDeleted, setShowDeleted] = useState(false);
  const [dialog, setDialog] = useState<DialogState | null>(null);

  const linkedRowId = searchParams.get(ROW_PARAM);
  // A link to a row opens it in view mode until the panel is closed
  const activeDialog: DialogState | null =
    dialog ?? (linkedRowId ? { mode: 'view', rowId: linkedRowId } : null);
  const activeRow = list.accommodations.find((row) => row.id === activeDialog?.rowId);

  const deletedCount = list.accommodations.filter(isRowDeleted).length;
  const visibleRows = useMemo(
    () => (showDeleted ? list.accommodations : list.accommodations.filter((row) => !isRowDeleted(row))),
    [list.accommodations, showDeleted]
  );

  const closeDialog = () => {
    setDialog(null);
    if (linkedRowId) {
      searchParams.delete(ROW_PARAM);
      setSearchParams(searchParams, { replace: true });
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle>
          Alojamientos preinscriptos
          <span className="ml-2 text-sm font-normal text-gray-500">
            ({list.accommodations.length - deletedCount})
          </span>
        </CardTitle>
        <div className="flex flex-wrap gap-2">
          <ExportPreRegistrationsButton
            params={{ municipalListId: list.id, includeDeleted: showDeleted || undefined }}
          />
          {canEdit && (
            <Button onClick={() => setDialog({ mode: 'create' })}>
              <Plus className="mr-2 h-4 w-4" />
              Agregar alojamiento
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {deletedCount > 0 && (
          <div className="flex items-center gap-2">
            <Checkbox
              id="show-deleted-rows"
              checked={showDeleted}
              onCheckedChange={(checked) => setShowDeleted(checked === true)}
            />
            <Label htmlFor="show-deleted-rows" className="font-normal">
              Mostrar eliminados ({deletedCount})
            </Label>
          </div>
        )}

        {visibleRows.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <Inbox className="h-8 w-8 text-gray-400" />
            <p className="text-sm text-gray-600">Este listado todavía no tiene alojamientos.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <MunicipalListAccommodationsTable
              rows={visibleRows}
              canEdit={canEdit}
              departmentId={list.municipality.departmentId}
              onView={(row) => setDialog({ mode: 'view', rowId: row.id })}
              onEdit={(row) => setDialog({ mode: 'edit', rowId: row.id })}
            />
          </div>
        )}
      </CardContent>

      {/* A link to a row that is not in this list opens nothing */}
      {activeDialog && (activeDialog.mode === 'create' || activeRow) && (
        <PreRegisteredAccommodationSheet
          open
          onOpenChange={(open) => !open && closeDialog()}
          mode={activeDialog.mode}
          listId={list.id}
          municipalityId={list.municipalityId}
          departmentId={list.municipality.departmentId}
          row={activeRow}
          canEdit={canEdit}
        />
      )}
    </Card>
  );
};
